import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Link from "@mui/material/Link";
import { useEffect, useState } from "react";
import FruitifyLogo from "../../../shared-components/FruitifyLogo";
import {
  RegistrationCard,
  FruitifyStack,
  FruitifyContainer,
} from "../../../shared-components/Containers";
import isPasswordValidFormat from "../Utils";
import { Checkbox, FormControlLabel, Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useApiClient } from "../../../api";
import GeneralLoading from "../../../shared-components/Loading";
import { enqueueSnackbar } from "notistack";
import SchoolSearchAutocomplete from "../../school/components/SchoolSearchAutocomplete";
import TermsAcceptance from "../components/TermsAcceptance";
import { useTrackingAnalytics } from "../../../hooks/useTrackingAnalytics";
import { SingleSignOn } from "../components/SingleSignOn";

const validateName = (name, fieldName) => {
  if (!name) {
    return `${fieldName} must be provided`;
  } else if (!/^[A-Za-z]+$/.test(name)) {
    return `${fieldName} can only contain letters`;
  } else {
    return "";
  }
};

export default function Register() {
  const [errors, setErrors] = useState({});
  const [isHomeSchooler, setIsHomeSchooler] = useState(false);
  const [homeSchool, setHomeSchool] = useState({});
  const { recordMarketingEvent } = useTrackingAnalytics();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    school: null,
  });

  const [termsAndConditions, setTermsAndConditions] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const apiClient = useApiClient();

  useEffect(() => {
    recordMarketingEvent("register_landing", {}, true);
    console.log("Redirecting to free account request page");
    navigate("free-account-request");
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await apiClient.core.getRegistrationInformation();
        setTermsAndConditions(result?.termsAndConditions || null);
        setHomeSchool(result?.homeSchool || {});
      } catch (error) {
        console.error("Failed to fetch registration data or terms:", error);
        setTermsAndConditions(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const onRegister = () => {
    if (validateData()) {
      handleSubmit();
    }
  };

  const handleChange = (field, value) => {
    if (field === "school" && typeof value === "string") {
      value = value
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateData = () => {
    const newErrors = {};

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Incorrect or missing email";
    }

    const firstNameError = validateName(formData.firstName, "First name");
    if (firstNameError) {
      newErrors.firstName = firstNameError;
    }
    const surnameError = validateName(formData.lastName, "Surname");
    if (surnameError) {
      newErrors.lastName = firstNameError;
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.password = "Passwords do not match";
    }

    const passwordError = isPasswordValidFormat(
      formData.password,
      null,
      null,
      true,
    );

    if (passwordError) {
      newErrors.password = passwordError;
    }
    if (!formData.school) {
      newErrors.school = "School is required";
    }

    const isValid = Object.keys(newErrors).length === 0;
    setErrors(newErrors);
    return isValid;
  };

  const handleSuccessfulRegistration = () => {
    navigate("/auth/register/success");
  };

  const handleSubmit = async () => {
    try {
      const data = await apiClient.users.register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        school: formData.school.id,
        termsAndConditions: termsAndConditions.id,
      });
      if (data?.success) {
        enqueueSnackbar(
          "Registration Successful. An activation email has been sent to your email.",
          {
            variant: "success",
            autoHideDuration: 3000,
          },
        );
        handleSuccessfulRegistration();
      } else {
        enqueueSnackbar(data.message, {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error registering, please try again", {
        variant: "error",
        autoHideDuration: 3000,
      });
    }
  };

  const registerWithSSO = async (provider, token) => {
    if (!formData.school || !acceptedTerms) return;

    try {
      const data = await apiClient.users.registerWithSSO({
        school: formData.school.id,
        termsAndConditions: termsAndConditions.id,
        provider,
        token,
      });
      if (data.success) {
        handleSuccessfulRegistration();
      } else {
        enqueueSnackbar(data.message || "SSO registration failed", {
          variant: "error",
        });
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error registering via SSO", { variant: "error" });
    }
  };

  const handleHomeSchoolerChecked = (checked) => {
    if (checked) {
      handleChange("school", homeSchool);
    } else {
      handleChange("school", null);
    }
    setIsHomeSchooler(checked);
  };

  if (loading) {
    return <GeneralLoading />;
  }

  if (!loading && !termsAndConditions?.preSignedUrl) {
    return <>There has been a problem.</>;
  }

  return (
    <FruitifyContainer>
      <FruitifyStack
        direction="column"
        justifyContent="space-between"
        sx={{ height: "100%" }}
      >
        <RegistrationCard variant="outlined">
          <Stack direction={"row"} justifyContent={"center"}>
            <FruitifyLogo />
          </Stack>
          <Typography
            component="h1"
            variant="h4"
            sx={{ width: "100%", fontSize: "clamp(2rem, 10vw, 2.15rem)" }}
          >
            Register
          </Typography>
          <Box
            component="form"
            onSubmit={(e) => handleSubmit(e)}
            noValidate
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              gap: 2,
            }}
          >
            {loading ? (
              <GeneralLoading />
            ) : (
              <Grid container spacing={2}>
                <Grid item size={12}>
                  {!isHomeSchooler && (
                    <SchoolSearchAutocomplete
                      school={formData.school}
                      onChange={(_, value) => handleChange("school", value)}
                    />
                  )}
                </Grid>
                <Grid item size={12} sx={{ mt: -1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isHomeSchooler}
                        onChange={(e) =>
                          handleHomeSchoolerChecked(e.target.checked)
                        }
                      />
                    }
                    label="I am a home-educating parent."
                    labelPlacement="right"
                  />
                </Grid>
                <TermsAcceptance
                  termsUrl={termsAndConditions?.preSignedUrl}
                  checked={acceptedTerms}
                  onChange={setAcceptedTerms}
                />
                <SingleSignOn
                  school={formData.school}
                  acceptedTerms={acceptedTerms}
                  onSuccess={registerWithSSO}
                  registration
                />
                <Grid item size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="First Name"
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                    value={formData.firstName}
                    autoComplete="given-name"
                    onChange={(e) => handleChange("firstName", e.target.value)}
                    required
                    fullWidth
                    variant="outlined"
                  />
                </Grid>
                <Grid item size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Surname"
                    autoComplete="family-name"
                    value={formData.lastName}
                    onChange={(e) => handleChange("lastName", e.target.value)}
                    error={!!errors.lastName}
                    helperText={errors.lastName}
                    required
                    fullWidth
                    variant="outlined"
                  />
                </Grid>

                <Grid item size={12}>
                  <FormControl fullWidth>
                    <TextField
                      label="Email"
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      error={!!errors.email}
                      helperText={errors.email}
                      autoComplete="email"
                      autoFocus
                      required
                      fullWidth
                      variant="outlined"
                    />
                  </FormControl>
                </Grid>

                <Grid item size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="Password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => handleChange("password", e.target.value)}
                      error={!!errors.password}
                      helperText={errors.password}
                      autoComplete="new-password"
                      required
                      fullWidth
                      variant="outlined"
                    />
                  </FormControl>
                </Grid>

                <Grid item size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="Confirm Password"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) =>
                        handleChange("confirmPassword", e.target.value)
                      }
                      error={!!errors.confirmPassword}
                      helperText={errors.confirmPassword}
                      autoComplete="new-password"
                      required
                      fullWidth
                      variant="outlined"
                    />
                  </FormControl>
                </Grid>
              </Grid>
            )}
          </Box>
          <Button
            onClick={onRegister}
            color={"secondary"}
            variant="contained"
            disabled={!acceptedTerms}
          >
            Register
          </Button>

          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="100%"
          >
            <Link
              onClick={() => navigate(`/login`)}
              variant="contained"
              color={"secondary"}
              component="button"
            >
              Back to Login
            </Link>
          </Box>
        </RegistrationCard>
      </FruitifyStack>
    </FruitifyContainer>
  );
}
